import React, { useState } from 'react';
import Joyride, { STATUS, EVENTS } from 'react-joyride';

const LAST_STEP_INDEX = 9; // Index of the "Invite Riders to Book" step

// Custom tooltip that renders differently on the final step
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
  isLastStep,
  onInviteClick,
  onSkipInviteClick,
}) => {
  return (
    <div
      {...tooltipProps}
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      }}
    >
      {/* Title */}
      {step.title && (
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#1f2937' }}>
          {step.title}
        </h3>
      )}

      {/* Content */}
      <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#1f2937', marginBottom: '16px' }}>
        {step.content}
      </div>

      {/* Footer */}
      {isLastStep ? (
        // Final step: custom action buttons — must click invite or skip
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={onInviteClick}
            style={{
              backgroundColor: '#16a34a',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Invite Riders to Book
          </button>
          <button
            onClick={onSkipInviteClick}
            style={{
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Skip for Now
          </button>
        </div>
      ) : (
        // Standard steps: replicate Joyride-style navigation
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button
              {...skipProps}
              style={{ color: '#9ca3af', fontSize: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
            >
              Skip Tutorial
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              {index + 1}/{size}
            </span>
            {index > 0 && (
              <button
                {...backProps}
                style={{ color: '#6b7280', fontSize: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px', marginRight: '4px' }}
              >
                Back
              </button>
            )}
            <button
              {...primaryProps}
              style={{
                backgroundColor: '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardTutorial = ({ onComplete, onSkip, onOpenInviteFlow, onSkipInviteStep }) => {
  const [run, setRun] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = [
    {
      target: '[data-tour="pending-requests"]',
      title: 'Pending Ride Requests',
      content: (
        <div>
          <p className="mb-2">View and respond to ride requests here.</p>
          <p className="mb-2">You can <strong>Accept</strong>, <strong>Decline</strong>, or send a <strong>Counter-Offer</strong> with adjusted pricing.</p>
          <p className="text-sm text-gray-600 mt-3">
            <strong>Why Counter-Offer?</strong> An 8-mile ride in Downtown Dallas is very different from an 8-mile ride in Lubbock.
            Counter-offers let you send a flat fee to the rider that better reflects the actual ride conditions. This flat fee is the GROSS AMOUNT the rider will pay, and will be subject to your applicable platform fee (i.e. 3% for Captain Tier)
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'bottom'
    },
    {
      target: '[data-tour="upcoming-rides"]',
      title: 'Upcoming Rides Calendar',
      content: (
        <div>
          <p className="mb-2">See all your scheduled rides at a glance.</p>
          <p>Click on any ride to view details, contact the rider, or get directions to the pickup location.</p>
        </div>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="payouts"]',
      title: 'Payout History',
      content: (
        <div>
          <p className="mb-2">Track your earnings, pending payouts, and transaction history.</p>
          <p>Payouts are processed automatically every day and sent directly to your Stripe connected bank account once the funds from that ride have cleared in Stripe (typically 1-2 business days).</p>
        </div>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="availability"]',
      title: 'Manage Availability',
      content: (
        <div>
          <p className="mb-2">Update when you're available to accept rides.</p>
          <p>Set different hours for each day of the week, and add blackout periods for vacations or time off.</p>
        </div>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="pricing"]',
      title: 'Set Pricing',
      content: (
        <div>
          <p className="mb-2">Control your per-mile rates for different times.</p>
          <p>You can set different rates for peak hours vs. off-peak, or adjust pricing for specific days of the week.</p>
        </div>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="active-rides"]',
      title: 'Active Rides',
      content: (
        <div>
          <p className="mb-2">Currently in-progress rides appear here.</p>
          <p>When a ride starts, you'll see it move from "Upcoming" to "Active" where you can mark it as completed.</p>
        </div>
      ),
      placement: 'top'
    },
    {
      target: '[data-tour="week-calendar"]',
      title: 'Weekly Schedule',
      content: (
        <div>
          <p className="mb-2">An overview of your week at a glance.</p>
          <p>See your scheduled rides and available time slots without switching to the full calendar view.</p>
        </div>
      ),
      placement: 'top'
    },
    {
      target: '[data-tour="recent-rides"]',
      title: 'Recent Rides',
      content: (
        <div>
          <p className="mb-2">View your recently completed rides.</p>
          <p>Access the full Ride History page to see detailed earnings metrics, ride statistics, and historical data.</p>
        </div>
      ),
      placement: 'top'
    },
    {
      target: '[data-tour="settings"]',
      title: 'Settings & Profile',
      content: (
        <div>
          <p className="mb-2">Edit your profile, vehicle information, and account settings.</p>
          <p>Keep your information up-to-date so riders know who they're booking with.</p>
        </div>
      ),
      placement: 'left'
    },
    {
      target: '[data-tour="share-link"]',
      title: 'Invite Riders to Book',
      content: (
        <div>
          <p className="mb-2">Share your unique booking link with riders via QR code, a copyable link, or email invitation.</p>
          <p className="mb-2">Riders can scan your QR code or use your link to book rides directly with you!</p>
          <p className="text-sm text-green-600 font-medium">Share it on social media, business cards, or send directly to returning customers.</p>
        </div>
      ),
      placement: 'left'
    }
  ];

  // Handlers for the custom final step
  const handleInviteClick = () => {
    setRun(false);
    onOpenInviteFlow?.();
    onComplete?.();
  };

  const handleSkipInviteClick = () => {
    setRun(false);
    onSkipInviteStep?.();
    onComplete?.();
  };

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action } = data;

    // Handle step navigation in controlled mode
    if (type === EVENTS.STEP_AFTER && action === 'next') {
      if (index < LAST_STEP_INDEX) {
        setStepIndex(index + 1);
      }
      // Don't advance past last step — custom buttons handle it
      return;
    }

    if (type === EVENTS.STEP_AFTER && action === 'prev') {
      setStepIndex(index - 1);
      return;
    }

    // Handle skip (user clicked "Skip Tutorial")
    if (status === STATUS.SKIPPED) {
      setRun(false);
      onSkip?.();
    }
  };

  const customStyles = {
    options: {
      primaryColor: '#16a34a',
      textColor: '#1f2937',
      zIndex: 10000
    },
    spotlight: {
      borderRadius: '12px'
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      disableOverlayClose
      disableCloseOnEsc
      callback={handleJoyrideCallback}
      styles={customStyles}
      tooltipComponent={(props) => (
        <CustomTooltip
          {...props}
          isLastStep={stepIndex === LAST_STEP_INDEX}
          onInviteClick={handleInviteClick}
          onSkipInviteClick={handleSkipInviteClick}
        />
      )}
    />
  );
};

export default DashboardTutorial;
